import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

import I18nText from "../components/I18nText";
import LegalMetaRow from "../components/LegalMetaRow";
import PlainEnglish from "../components/PlainEnglish";
import { track } from "../lib/analytics";

/**
 * Shared renderer for /privacy and /terms.
 *
 * `ns` is the top-level i18n namespace ("privacy" or "terms"). The dictionary
 * shape matches across both pages:
 *   {ns}.title / sectionLabel / h1 / lede / meta[]
 *   {ns}.sections[] where each section is:
 *      { num, h2, blocks: [ {p|h3|ul|plain: ...} ] }
 *   {ns}.governance
 *   {ns}.contact.{h3, p1, p2, email}
 *
 * Per user spec, BrandMark is NEVER applied inside the legal body — blocks
 * render plain text (with inline <strong>/<em> allowed via Trans).
 */
export default function LegalPage({ ns }) {
    const { t } = useTranslation();
    const sections = t(`${ns}.sections`, { returnObjects: true }) || [];

    useEffect(() => {
        track(`${ns}_view`, {});
        document.title = t(`${ns}.title`);
    }, [ns, t]);

    const onMailto = () => track("legal_contact_click", { page: ns });
    const email = t(`${ns}.contact.email`);

    return (
        <div data-testid={`${ns}-page`}>
            <section className="legal-hero" data-testid={`${ns}-hero`}>
                <div className="section-label mono">{t(`${ns}.sectionLabel`)}</div>
                <h1><I18nText i18nKey={`${ns}.h1`} /></h1>
                <p className="legal-hero-lede">{t(`${ns}.lede`)}</p>
                <LegalMetaRow
                    i18nKey={`${ns}.meta`}
                    testId={`${ns}-meta`}
                />
            </section>

            <div className="legal-body">
                {sections.map((section, si) => (
                    <div
                        className="legal-section"
                        key={section.num}
                        data-testid={`${ns}-section-${section.num}`}
                    >
                        <h2>
                            <span className="num mono">{section.num}</span>
                            {section.h2}
                        </h2>
                        {(section.blocks || []).map((block, bi) => {
                            const [type] = Object.keys(block);
                            const keyPath = `${ns}.sections.${si}.blocks.${bi}.${type}`;
                            if (type === "p") {
                                return (
                                    <p key={bi}>
                                        <I18nText i18nKey={keyPath} />
                                    </p>
                                );
                            }
                            if (type === "h3") {
                                return <h3 key={bi}>{block.h3}</h3>;
                            }
                            if (type === "ul") {
                                return (
                                    <ul key={bi}>
                                        {block.ul.map((_item, li) => (
                                            <li key={li}>
                                                <I18nText
                                                    i18nKey={`${keyPath}.${li}`}
                                                />
                                            </li>
                                        ))}
                                    </ul>
                                );
                            }
                            if (type === "plain") {
                                return (
                                    <PlainEnglish key={bi}>
                                        <I18nText i18nKey={keyPath} />
                                    </PlainEnglish>
                                );
                            }
                            return null;
                        })}
                    </div>
                ))}

                <div className="legal-governance" data-testid={`${ns}-governance`}>
                    <em>{t(`${ns}.governance`)}</em>
                </div>

                <div className="legal-contact" data-testid={`${ns}-contact`}>
                    <h3>{t(`${ns}.contact.h3`)}</h3>
                    <p>
                        <I18nText
                            i18nKey={`${ns}.contact.p1`}
                            components={{
                                mail: (
                                    <a
                                        href={`mailto:${email}`}
                                        data-testid={`${ns}-contact-mail`}
                                        onClick={onMailto}
                                    >
                                        {email}
                                    </a>
                                ),
                            }}
                        />
                    </p>
                    <p>{t(`${ns}.contact.p2`)}</p>
                </div>
            </div>
        </div>
    );
}
